/**
 * Numbered pagination (1-based). Shows first/last, a window around the current
 * page, and `…` gaps. Pure presentational — the parent owns page state and
 * translates page → offset.
 */

function pageItems(page: number, pageCount: number): Array<number | '…'> {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const items: Array<number | '…'> = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);
  if (start > 2) items.push('…');
  for (let p = start; p <= end; p++) items.push(p);
  if (end < pageCount - 1) items.push('…');
  items.push(pageCount);
  return items;
}

export function Pagination({
  page,
  pageCount,
  onChange,
}: {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}) {
  if (pageCount <= 1) return null;

  const btn =
    'min-w-8 rounded border px-2.5 py-1.5 text-sm transition disabled:opacity-40';

  return (
    <nav className="mt-6 flex items-center justify-center gap-1" aria-label="Σελιδοποίηση">
      <button
        className={`${btn} border-slate-200 bg-white text-slate-700`}
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        aria-label="Προηγούμενη"
      >
        ←
      </button>
      {pageItems(page, pageCount).map((it, i) =>
        it === '…' ? (
          <span key={`gap-${i}`} className="px-1.5 text-sm text-slate-400">
            …
          </span>
        ) : (
          <button
            key={it}
            onClick={() => onChange(it)}
            aria-current={it === page ? 'page' : undefined}
            className={`${btn} font-medium ${
              it === page
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
            }`}
          >
            {it}
          </button>
        ),
      )}
      <button
        className={`${btn} border-slate-200 bg-white text-slate-700`}
        disabled={page >= pageCount}
        onClick={() => onChange(page + 1)}
        aria-label="Επόμενη"
      >
        →
      </button>
    </nav>
  );
}
